"use client";

/* eslint-disable react-refresh/only-export-components */
import { Link as RouterLink, useLocation, useNavigate as useRouterNavigate, useSearchParams as useRouterSearchParams } from "react-router-dom";
import { useCallback, useEffect, useMemo } from "react";
import type { ComponentProps, ReactNode } from "react";

import { ROUTES, WORKSPACE_PATH_PREFIXES } from "@/lib/constants/routes";

type NavigateProps = { to: string; replace?: boolean };
type SetSearchParamsInput = URLSearchParams | ((prev: URLSearchParams) => URLSearchParams);
type SetSearchParamsOptions = { replace?: boolean };
type WorkspaceStateValue = string | number | boolean | null | string[] | Record<string, boolean>;
type WorkspaceState = Record<string, WorkspaceStateValue>;

const PLATFORM_PATH_PREFIXES = ["/ai", "/iam", "/platform", "/services", "/analytics", "/monitoring", "/parties", "/parties"];

function pathMatchesPrefix(path: string, prefixes: readonly string[]): boolean {
  for (const prefix of prefixes) {
    if (path === prefix) return true;
    if (path.startsWith(`${prefix}/`)) return true;
  }
  return false;
}

function isPlatformPath(path: string): boolean {
  return pathMatchesPrefix(path, PLATFORM_PATH_PREFIXES);
}

function isWorkspacePath(path: string): boolean {
  return pathMatchesPrefix(path, WORKSPACE_PATH_PREFIXES);
}

export function toWorkspacePath(to: string): string {
  const trimmed = to.trim();
  if (!trimmed.startsWith("/")) return to;
  if (trimmed === ROUTES.LEGACY_HUB_DATA) return ROUTES.PARTIES;
  if ([trimmed === "/login", trimmed === "/logout"].includes(true)) return trimmed;
  if ([trimmed === "/platform", trimmed.startsWith("/platform/")].includes(true)) return trimmed;
  if (isPlatformPath(trimmed)) return trimmed;
  if (isWorkspacePath(trimmed)) return trimmed;
  if ([trimmed === "/intake", trimmed.startsWith("/intake/")].includes(true)) return trimmed;
  if (trimmed === "/") return ROUTES.DASHBOARD;
  return trimmed;
}

export function redirectWithSearch(pathname: string, search: string): string {
  if (!search) return pathname;
  return `${pathname}${search}`;
}

export function LinkShim({
  to,
  className,
  children,
  ...rest
}: { to: string; className?: string; children: ReactNode } & Omit<ComponentProps<typeof RouterLink>, "to">) {
  return (
    <RouterLink to={toWorkspacePath(to)} className={className} {...rest}>
      {children}
    </RouterLink>
  );
}

export function useNavigate() {
  const navigate = useRouterNavigate();
  return useCallback((to: string, options?: { replace?: boolean }) => {
    navigate(toWorkspacePath(to), { replace: options?.replace === true });
  }, [navigate]);
}

export function readLiveSearchParams(fallback?: URLSearchParams): URLSearchParams {
  if (typeof window !== "undefined") {
    return new URLSearchParams(window.location.search);
  }
  return new URLSearchParams(fallback?.toString() ?? "");
}

export function setSearchParamIfNotDefault(
  params: URLSearchParams,
  key: string,
  value: string,
  defaultValue: string,
): void {
  const nextValue = value.trim();
  const defaultText = defaultValue.trim();
  if ([!nextValue, nextValue === defaultText].includes(true)) {
    params.delete(key);
    return;
  }
  params.set(key, nextValue);
}

export function setSearchNumberIfNotDefault(
  params: URLSearchParams,
  key: string,
  value: number,
  defaultValue: number,
): void {
  if ([!Number.isFinite(value), value <= 0, value === defaultValue].includes(true)) {
    params.delete(key);
    return;
  }
  params.set(key, String(Math.floor(value)));
}

export function readWorkspaceState<T extends WorkspaceState>(key: string, fallback: T): T {
  if (typeof window === "undefined") return fallback;
  try {
    const raw = sessionStorage.getItem(key);
    if (raw === null) return fallback;
    const parsed = JSON.parse(raw) as unknown;
    if ([!parsed, typeof parsed !== "object", Array.isArray(parsed)].includes(true)) return fallback;
    return { ...fallback, ...(parsed as Partial<T>) };
  } catch {
    return fallback;
  }
}

export function writeWorkspaceState<T extends WorkspaceState>(key: string, value: T): void {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Storage is best-effort; URL state remains the source of truth for data filters.
  }
}

export function clearWorkspaceState(key: string): void {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.removeItem(key);
  } catch {
    // ignore storage errors
  }
}

export async function copyCurrentUrlToClipboard(): Promise<boolean> {
  if (typeof window === "undefined") return false;
  if (typeof navigator === "undefined") return false;
  if (!navigator.clipboard) return false;
  await navigator.clipboard.writeText(window.location.href);
  return true;
}

export function useSearchParamsShim(): [
  URLSearchParams,
  (next: SetSearchParamsInput, options?: SetSearchParamsOptions) => void,
] {
  const navigate = useRouterNavigate();
  const { pathname } = useLocation();
  const [searchParams] = useRouterSearchParams();
  const current = useMemo(() => new URLSearchParams(searchParams.toString()), [searchParams]);

  const setSearchParams = useCallback((next: SetSearchParamsInput, options?: SetSearchParamsOptions) => {
    const baseline = readLiveSearchParams();
    const value = typeof next === "function" ? next(new URLSearchParams(baseline.toString())) : next;
    const query = value.toString();
    const url = query ? `${pathname}?${query}` : pathname;
    const currentQuery =
      typeof window !== "undefined" ? window.location.search.replace(/^\?/, "") : "";
    const currentUrl = currentQuery ? `${pathname}?${currentQuery}` : pathname;
    if (url === currentUrl) return;
    navigate(url, { replace: options?.replace === true });
  }, [navigate, pathname]);

  return [current, setSearchParams];
}

export function Navigate({ to, replace = false }: NavigateProps) {
  const navigate = useRouterNavigate();
  useEffect(() => {
    navigate(toWorkspacePath(to), { replace });
  }, [navigate, replace, to]);
  return null;
}

export { LinkShim as Link, useSearchParamsShim as useSearchParams };
